/**
 * Slide toggle utility for smooth expand/collapse animations
 * Handles ARIA attributes and visibility classes
 */
class SlideToggle {
  constructor(trigger, element, options = {}) {
    this.trigger = trigger; // The button/element that triggers the toggle
    this.element = element; // The element to show/hide
    this.duration = options.duration || 300;
    this.easing = options.easing || "ease-in-out";
    this.useHidden = options.useHidden !== false; // Default true, can be disabled
    this.toggleClass = options.toggleClass || "is-open"; // Class to add when open
    this.isAnimating = false;
    this.isExpanded = this.trigger.getAttribute("aria-expanded") === "true";

    this.init();
  }

  init() {
    // Set initial ARIA state if not set
    if (!this.trigger.hasAttribute("aria-expanded")) {
      this.trigger.setAttribute("aria-expanded", "false");
    }

    // Add click listener to trigger
    this.trigger.addEventListener("click", () => this.toggle());
  }

  /**
   * Slide down (show element)
   */
  slideDown(callback) {
    if (this.isAnimating) return;

    this.isAnimating = true;

    if (this.useHidden) {
      this.element.hidden = false;
    } else {
      this.element.classList.add(this.toggleClass);
    }

    this.trigger.setAttribute("aria-expanded", "true");
    this.isExpanded = true;

    const height = this.element.scrollHeight;

    this.element.removeAttribute("data-skip-playback");
    this.element.style.overflow = "hidden";
    this.element.style.height = "0";
    this.element.style.transition = `height ${this.duration}ms ${this.easing}`;

    // Force reflow
    this.element.offsetHeight;

    this.element.style.height = `${height}px`;

    const onTransitionEnd = () => {
      this.element.style.height = "";
      this.element.style.overflow = "";
      this.element.style.transition = "";
      this.isAnimating = false;
      this.element.removeEventListener("transitionend", onTransitionEnd);

      if (callback) callback();
    };

    this.element.addEventListener("transitionend", onTransitionEnd);
  }

  /**
   * Slide up (hide element)
   */
  slideUp(callback) {
    if (this.isAnimating) return;

    this.isAnimating = true;
    this.trigger.setAttribute("aria-expanded", "false");
    this.isExpanded = false;

    const height = this.element.scrollHeight;

    this.element.setAttribute("data-skip-playback", "true");
    this.element.style.overflow = "hidden";
    this.element.style.height = `${height}px`;

    // Force reflow
    this.element.offsetHeight;

    this.element.style.transition = `height ${this.duration}ms ${this.easing}`;
    this.element.style.height = "0";

    const onTransitionEnd = () => {
      if (this.useHidden) {
        this.element.hidden = true;
      } else {
        this.element.classList.remove(this.toggleClass);
      }

      this.element.style.height = "";
      this.element.style.overflow = "";
      this.element.style.transition = "";
      this.isAnimating = false;
      this.element.removeEventListener("transitionend", onTransitionEnd);

      if (callback) callback();
    };

    this.element.addEventListener("transitionend", onTransitionEnd);
  }

  /**
   * Toggle between slide up and slide down
   */
  toggle(callback) {
    const isClosed = this.useHidden
      ? this.element.hidden || !this.isExpanded
      : !this.element.classList.contains(this.toggleClass) || !this.isExpanded;

    if (isClosed) {
      this.slideDown(callback);
    } else {
      this.slideUp(callback);
    }
  }

  /**
   * Expand without animation (useful for initial state)
   */
  expand() {
    if (this.useHidden) {
      this.element.hidden = false;
    } else {
      this.element.classList.add(this.toggleClass);
    }

    this.trigger.setAttribute("aria-expanded", "true");
    this.isExpanded = true;
  }

  /**
   * Collapse without animation (useful for initial state)
   */
  collapse() {
    if (this.useHidden) {
      this.element.hidden = true;
    } else {
      this.element.classList.remove(this.toggleClass);
    }

    this.trigger.setAttribute("aria-expanded", "false");
    this.isExpanded = false;
  }
}

export default SlideToggle;
